import React from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "outline" | "destructive" | "ghost";
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
  asChild?: boolean;
}

export function Button({
  variant = "default",
  size = "md",
  className = "",
  children,
  asChild = false,
  ...props
}: ButtonProps) {
  const baseStyles =
    "font-semibold rounded-lg transition-colors inline-block " +
    // focus-visible keeps ring for keyboard; hidden for mouse (WCAG 2.4.11)
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-keiro-green focus-visible:ring-offset-1 " +
    "disabled:pointer-events-none disabled:opacity-50";

  const variantStyles = {
    default:     "bg-keiro-green text-white hover:bg-keiro-green-dark",
    outline:     "border-2 border-keiro-green/30 text-keiro-green-dark hover:bg-keiro-mist",
    destructive: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500",
    ghost:       "text-keiro-gray-700 hover:bg-keiro-mist",
  };

  // min-h-11 (44px) — satisfies WCAG 2.5.8 target size
  const sizeStyles = {
    sm: "px-3 py-2 text-sm min-h-[44px]",
    md: "px-4 py-2.5 text-base min-h-[44px]",
    lg: "px-8 py-4 text-lg min-h-[52px]",
  };

  const styles = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;

  if (asChild) {
    return React.cloneElement(children as React.ReactElement, {
      className: styles,
      ...props,
    });
  }

  return (
    <button
      className={styles}
      {...props}
    >
      {children}
    </button>
  );
}
